<template>
    <div>
        <div class="x-stat">
            <a-row :gutter="24">
                <a-col :span="2">
                    <a-statistic title="男用户" :value="maleSummary" />
                </a-col>
                <a-col :span="2">
                    <a-statistic title="女用户" :value="femaleSummary" />
                </a-col>
                <a-col :span="2">
                    <a-statistic title="充值" :value="summary.rechargeAmount" />
                </a-col>
                <a-col :span="2">
                    <a-statistic title="提现" :value="summary.withdrawAmount" />
                </a-col>
                <a-col :span="2">
                    <a-statistic title="差额" :value="varianceText" :value-style="varianceStyle" />
                </a-col>
            </a-row>
        </div>

        <a-card :bordered="false">
            <div class="x-filter">
                <a-form layout="inline">
                    <a-row :gutter="24">
                        <a-col :span="4">
                            <a-form-item label="地区">
                                <a-input v-model="queryParams.area" placeholder="地区名称" allow-clear />
                            </a-form-item>
                        </a-col>
                        <a-col :span="6">
                            <a-form-item label="时间">
                                <a-range-picker
                                    v-model="statDate"
                                    style="width: 100%"
                                    @change="onStatDate"
                                />
                            </a-form-item>
                        </a-col>
                        <a-col :span="4">
                            <span class="x-filter-submit-box">
                                <a-button type="primary" @click="search">查询</a-button>
                                <a-button style="margin-left: 8px" @click="reset">重置</a-button>
                            </span>
                        </a-col>
                    </a-row>
                </a-form>
            </div>

            <a-table
                :rowKey="record => record.id"
                :columns="tableColumns"
                :loading="tableLoading"
                :dataSource="tableData"
                :pagination="false"
                bordered
                size="middle"
            >
                <span slot="recharge" slot-scope="record">
                    {{ record.rechargeAmount }} / {{ record.rechargeNum }}
                </span>
                <span slot="withdraw" slot-scope="record">
                    {{ record.withdrawAmount }} / {{ record.withdrawNum }}
                </span>
                <span
                    slot="variance"
                    slot-scope="record"
                    :class="record.varianceAmount > 0 ? 'variance-up' : (record.varianceAmount < 0 ? 'variance-down' : '')"
                >
                    {{ formatVariance(record.varianceAmount) }}
                </span>
            </a-table>

            <div class="x-pagination">
                <a-pagination
                    v-model="pageIndex"
                    :pageSize="pageSize"
                    :total="totalRow"
                    :hideOnSinglePage="true"
                    show-less-items
                    @change="pageChange"
                />
            </div>
        </a-card>
    </div>
</template>

<script>
import moment from 'moment';
import { statAreaList } from '@/api/statistics/area';

const emptySummary = () => ({
    newMaleNum: 0,
    activeMaleNum: 0,
    maleNum: 0,
    newFemaleNum: 0,
    activeFemaleNum: 0,
    femaleNum: 0,
    rechargeAmount: 0,
    withdrawAmount: 0,
    varianceAmount: 0
});

const tableColumns = [
    {
        title: '日期',
        dataIndex: 'statDate',
        width: 120
    },
    {
        title: '地区',
        dataIndex: 'areaName',
        width: 140
    },
    {
        title: '用户',
        children: [
            { title: '男', dataIndex: 'maleNum', width: 70, align: 'center' },
            { title: '女', dataIndex: 'femaleNum', width: 70, align: 'center' }
        ]
    },
    {
        title: '新增',
        children: [
            { title: '男', dataIndex: 'newMaleNum', width: 70, align: 'center' },
            { title: '女', dataIndex: 'newFemaleNum', width: 70, align: 'center' }
        ]
    },
    {
        title: '活跃',
        children: [
            { title: '男', dataIndex: 'activeMaleNum', width: 70, align: 'center' },
            { title: '女', dataIndex: 'activeFemaleNum', width: 70, align: 'center' }
        ]
    },
    {
        title: '盈亏',
        children: [
            {
                title: '充值',
                scopedSlots: { customRender: 'recharge' },
                width: 120,
                align: 'center'
            },
            {
                title: '提现',
                scopedSlots: { customRender: 'withdraw' },
                width: 120,
                align: 'center'
            },
            {
                title: '差额',
                scopedSlots: { customRender: 'variance' },
                width: 100,
                align: 'center'
            }
        ]
    },
    {
        title: '创建时间',
        dataIndex: 'created',
        width: 170
    }
];

export default {
    name: 'AreaStat',
    data() {
        return {
            tableColumns,
            tableData: [],
            tableLoading: false,
            totalRow: 0,
            pageSize: 20,
            pageIndex: 1,
            queryParams: {},
            statDate: [],
            summary: emptySummary()
        };
    },
    computed: {
        maleSummary() {
            const s = this.summary;
            return `${s.maleNum}/${s.newMaleNum}/${s.activeMaleNum}`;
        },
        femaleSummary() {
            const s = this.summary;
            return `${s.femaleNum}/${s.newFemaleNum}/${s.activeFemaleNum}`;
        },
        varianceText() {
            return this.formatVariance(this.summary.varianceAmount);
        },
        varianceStyle() {
            const amount = this.summary.varianceAmount;
            if (amount > 0) {
                return { color: '#52c41a' };
            }
            if (amount < 0) {
                return { color: '#f5222d' };
            }
            return {};
        }
    },
    created() {
        this.fetchTableData();
    },
    methods: {
        fetchTableData() {
            this.tableLoading = true;
            const params = {
                ...this.queryParams,
                page: this.pageIndex,
                pageSize: this.pageSize
            };
            return statAreaList(params).then(res => {
                this.tableData = res.data.recordList || [];
                this.totalRow = res.data.totalRow || 0;
                this.summary = {
                    ...emptySummary(),
                    ...(res.data.summary || {})
                };
                this.tableLoading = false;
            }).catch(() => {
                this.tableLoading = false;
            });
        },
        search() {
            this.pageIndex = 1;
            this.fetchTableData();
        },
        reset() {
            this.queryParams = {};
            this.statDate = [];
            this.pageIndex = 1;
            this.fetchTableData();
        },
        pageChange(page) {
            this.pageIndex = page;
            this.fetchTableData();
        },
        onStatDate(values) {
            if (values && values.length === 2) {
                this.queryParams.startStatDate = moment(values[0]).format('YYYY-MM-DD');
                this.queryParams.endStatDate = moment(values[1]).format('YYYY-MM-DD');
            } else {
                delete this.queryParams.startStatDate;
                delete this.queryParams.endStatDate;
            }
        },
        formatVariance(amount) {
            const value = Number(amount) || 0;
            if (value > 0) {
                return `+${value}`;
            }
            return String(value);
        }
    }
};
</script>

<style scoped lang="less">
.variance-up {
    color: #52c41a;
}

.variance-down {
    color: #f5222d;
}
</style>
